import { WidgetSectionItemSeverity } from '../../../../ui/gzWidgetSectionItem/types';
import {
  PolicyComplianceWithOverview,
  ProductUpdateInfo,
  SignatureUpdateInfo,
  MacOsPermissionIssues,
  PermissionIssues,
  PolicyComplianceInfo,
  EndpointIssues,
  ProductUpdateStatus,
  SignatureUpdateStatus,
} from '@nirvana/shared/dtos/types/widgets/common';

// Widget data

export type HealthDashboardInsightsData =
  | EndpointIssues
  | PolicyComplianceWithOverview
  | MacOsPermissionIssues
  | ProductUpdateStatus
  | SignatureUpdateStatus;

export interface InsightsDataOverview {
  healthy: number;
  warning: number;
  critical: number;
}

export interface EndpointIssuesDataSectionSecurity {
  //notScanned: number; //TODO RE-ENABLE WHEN ENDPOINT SENDS THIS STATUS
  unsolvedMalwareDetections: number;
  settingsChangedWithPowerUser: number;
}

export interface EndpointIssuesDataSectionCommunication {
  cannotConnectToGPN: number;
  isolated: number;
}

export interface EndpointIssuesDataSectionModule {
  disabledModules: number;
  malfunctioningModules: number;
  expiredModules: number;
  modulesInGracePeriod: number;
}

// Widget strings

export interface HealthDashboardInsightsStrings {
  sections: InsightsStringsSection[];
}

export interface InsightsStringsSection {
  key: string;
  title?: string;
  items: InsightsStringsItem[];
}

export interface InsightsStringsItem {
  key: string;
  name: string;
  tooltipText?: string;
  severity: WidgetSectionItemSeverity;
}

export interface HealthDashboardEndpointIssuesStrings extends HealthDashboardInsightsStrings {
  sections: EndpointIssuesStringsSection[];
}

export interface EndpointIssuesStringsSection extends InsightsStringsSection {
  key: keyof EndpointIssues;
  items: EndpointIssuesStringsItem[];
}

export interface EndpointIssuesStringsItem extends InsightsStringsItem {
  key: keyof (EndpointIssuesDataSectionSecurity &
    EndpointIssuesDataSectionCommunication &
    EndpointIssuesDataSectionModule);
}

export interface HealthDashboardPoliciesStrings extends HealthDashboardInsightsStrings {
  sections: PoliciesStringsSection[];
}

export interface PoliciesStringsSection extends InsightsStringsSection {
  items: PoliciesStringsItem[];
}

export interface PoliciesStringsItem extends InsightsStringsItem {
  key: keyof PolicyComplianceInfo;
}

export interface HealthDashboardMacOsStrings extends HealthDashboardInsightsStrings {
  sections: MacOsStringsSection[];
}

export interface MacOsStringsSection extends InsightsStringsSection {
  key: keyof MacOsPermissionIssues;
  items: MacOSPermissionsItem[];
}

export interface MacOSPermissionsItem extends InsightsStringsItem {
  key: keyof PermissionIssues;
}

export interface ProductUpdateItem extends InsightsStringsItem {
  key: keyof ProductUpdateInfo;
}

export interface ProductUpdateSection extends InsightsStringsSection {
  key: keyof ProductUpdateStatus;
  items: ProductUpdateItem[];
}

export interface HealthDashboardProductUpdateStrings extends HealthDashboardInsightsStrings {
  sections: ProductUpdateSection[];
}

export interface SignatureUpdateItem extends InsightsStringsItem {
  key: keyof SignatureUpdateInfo;
}

export interface SignatureUpdateSection extends InsightsStringsSection {
  key: keyof SignatureUpdateStatus;
  items: SignatureUpdateItem[];
}

export interface HealthDashboardSignatureUpdateStrings extends HealthDashboardInsightsStrings {
  sections: SignatureUpdateSection[];
}

export enum InsightsTemplatesStringKeys {
  UPDATED = 'updated',
  PENDING_RESTART = 'pendingRestart',
  UPDATE_DISABLED = 'updateDisabled',
  OUTDATED = 'outdated',
  OFFLINE_MORE_THAN_ONE_DAY = 'offlineMoreThanOneDay',
  COMPLIANT = 'compliant',
  ENDPOINT_COMMUNICATION_ISSUES = 'endpointCommunicationIssues',
  NON_COMPLIANT = 'nonCompliant',
  OFFLINE_SINCE_LAST_CHANGE = 'offlineSinceLastChange',
  EDITED_BY_POWER_USER = 'editedByPowerUser',
  INHERITED = 'inherited',
  DIRECTLY_APPLIED = 'directlyApplied',
  ASSIGNED_WITH_POLICY_RULES = 'assignedWithPolicyRules',
  ENDPOINT_SECURITY_ISSUES = 'endpointSecurityIssues',
  UNSOLVED_MALWARE_DETECTIONS = 'unsolvedMalwareDetections',
  SETTINGS_CHANGED_WITH_POWER_USER = 'settingsChangedWithPowerUser',
  CANNOT_CONNECT_TO_GPN = 'cannotConnectToGPN',
  ISOLATED = 'isolated',
  DISABLED_MODULES = 'disabledModules',
  MALFUNCTIONING_MODULES = 'malfunctioningModules',
  EXPIRED_MODULES = 'expiredModules',
  MODULES_IN_GRACE_PERIOD = 'modulesInGracePeriod',
  ENDPOINT_MODULE_ISSUES = 'endpointModuleIssues',
  POLICY_COMPLIANCE = 'policyCompliance',
  PRODUCT_UPDATE = 'productUpdate',
  SIGNATURE_UPDATE = 'signatureUpdate',
  MAC_OS_PERMISSION_ISSUES = 'permissionIssues',
  KERNEL_EXTENSION_NOT_APPROVED = 'kernelExtensionNotApproved',
  DISK_ACCESS_NOT_GRANTED = 'diskAccessNotGranted',
  NETWORK_EXTENSION_NOT_INSTALLED = 'networkExtensionNotInstalled',
  TUNNELING_APPLICATION_NOT_ALLOWED = 'tunnelingApplicationNotAllowed',
  CERTIFICATE_SSL_NOT_TRUSTED = 'certificateSSLNotTrusted',
  PRODUCT_UPDATE_STATUS = 'productUpdateStatus',
  PRODUCT_INFO = 'productInfo',
  SIGNATURE_UPDATE_STATUS = 'signatureUpdateStatus',
  SIGNATURE_INFO = 'signatureInfo',
}
